import React, { type FC, use, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Separator } from "@heroui/react";
import { FileUploaderContext, FileUploaderControlContext } from "../../context/FileUploaderContext";
import { FormattedMessage } from "react-intl";
import { FilesSameHashItem } from "./FilesSameHashItem";
import { type TUploadFile, UploadFileStatus } from "@dms/types";

export const FilesSameHash: FC = () => {
    const [processedFiles, setProcessedFiles] = useState<Record<string, number>>({});

    const { deleteAllNotUniq, confirmChoiceNotUniq } = use(FileUploaderControlContext);
    const { allFilesInfo } = use(FileUploaderContext);

    const notUniqueFiles = useMemo(
        () => allFilesInfo.filter((el) => el.status === UploadFileStatus.NOT_UNIQ),
        [allFilesInfo],
    );

    const sameHashFilesMap = useMemo(() => {
        if (!notUniqueFiles.length) {
            return null;
        }

        const obj: Record<string, TUploadFile[]> = {};
        notUniqueFiles.forEach((el) => {
            if (!obj[el.hash]) {
                obj[el.hash] = [];
            }
            obj[el.hash].push(el);
        });

        return obj;
    }, [notUniqueFiles]);

    useEffect(() => {
        setProcessedFiles((prev) => {
            const copyPrev = { ...prev };
            Object.keys(sameHashFilesMap || {}).forEach((key) => {
                if (prev[key]) {
                    return;
                }

                copyPrev[key] = 0;
            });

            return copyPrev;
        });
    }, [sameHashFilesMap]);

    const handleSelectNotUniqFiles = useCallback((key: string, idx: number) => {
        setProcessedFiles((prev) => {
            return { ...prev, [key]: idx };
        });
    }, []);

    const handleConfirm = useCallback(() => {
        confirmChoiceNotUniq(processedFiles, sameHashFilesMap);
        setProcessedFiles({});
    }, [confirmChoiceNotUniq, processedFiles, sameHashFilesMap]);

    const handleDeleteAll = useCallback(() => {
        deleteAllNotUniq(processedFiles, sameHashFilesMap);
        setProcessedFiles({});
    }, [deleteAllNotUniq, processedFiles, sameHashFilesMap]);

    if (!sameHashFilesMap) {
        return null;
    }

    return (
        <div>
            <Separator />
            <div className="flex flex-col gap-2 w-full">
                <span className="text-muted">
                    <FormattedMessage id="app.dms.identicalFiles" />
                </span>
                {Object.keys(sameHashFilesMap).map((hash) => {
                    return (
                        <FilesSameHashItem
                            key={hash}
                            hash={hash}
                            files={sameHashFilesMap[hash]}
                            handleSelectNotUniqFiles={handleSelectNotUniqFiles}
                        />
                    );
                })}
                <div className="flex justify-end gap-2 w-full">
                    <Button variant="secondary" onPress={handleConfirm}>
                        <FormattedMessage id="app.dms.confirmResolving" />
                    </Button>
                    <Button variant="danger-soft" onPress={handleDeleteAll}>
                        <FormattedMessage id="app.dms.deleteAll" />
                    </Button>
                </div>
            </div>
            <Separator />
        </div>
    );
};
